
import axios, { AxiosResponse } from 'axios'
import { Component, Watch, Prop, Mixins } from 'vue-property-decorator'
import debounce from 'lodash.debounce'
import { PluralResponse } from 'coloquent'
import Icon from '@/components/Icon.vue'
import { Facility } from '@/models/Facility'
import Direction from '@/directions/Direction'
import FacilityDirection from '@/directions/types/FacilityDirection'
import CreatesItemFromMapboxFeature from '@/mixins/CreatesItemFromMapboxFeature'
import User from '@/models/User'
import Testable from '@/mixins/Testable'

import { MAPBOX_PARAMS } from '@/plugins/mapbox-config'

@Component({
	components: {
		Icon
	}
})
export default class MapDirectionsFormInput extends Mixins(CreatesItemFromMapboxFeature, Testable) {
	query: string = ''
	isLoading: boolean = false
	debounceInput: any
	minQueryLength: number = 3
	items: Array<any> = []
	isValueFromParent: boolean = false

	@Prop({ default: false })
	showReverseButton!: boolean

	@Prop({ default: true })
	showMapMarker!: boolean

	@Prop({ default: true })
	showCurrentLocationButton!: boolean

	@Prop({ default: false })
	showBackButton!: boolean

	@Prop({ required: true })
	value!: Direction | null

	@Prop({ default: false })
	loading!: boolean

	@Prop({ default: false })
	fullWidth!: boolean

	@Prop({ default: null })
	facilityType!: string | null

	@Prop({ default: null })
	inputType!: string | null

	@Prop({ required: false, default: true })
	includeAddresses!: boolean

	@Prop({ default: null })
	canTransferLiquidBulk!: boolean | null

	@Prop({ required: false, default: false })
	disabled!: boolean

	@Prop({ required: false, default: false })
	allowFreeText!: boolean

	async created() {
		this.debounceInput = debounce(this.sendQuery, 500)
		if (this.inputType === 'loadingPlace') {
			const frequentlyUsedTerminals = await User.fetchFrequentlyUsedStartTerminalsLazy()
			this.items = this.mapFacilitiesToDirectionLocations(frequentlyUsedTerminals)
		} else if (this.inputType === 'unloadingPlace') {
			const frequentlyUsedTerminals = await User.fetchFrequentlyUsedEndTerminalsLazy()
			this.items = this.mapFacilitiesToDirectionLocations(frequentlyUsedTerminals)
		}
	}

	@Watch('value', { immediate: true })
	setQueryFromValue() {
		if (!this.value) {
			return
		}

		this.isValueFromParent = true
		this.query = this.value.text

		this.$nextTick(() => {
			this.isValueFromParent = false
		})
	}

	focus() {
		;(this.$refs.input as HTMLInputElement).focus()
	}

	@Watch('query')
	search(value: any) {
		if (this.isValueFromParent) {
			return
		}

		if (!value) {
			return
		}

		const trimmedQuery = value?.trim ? value.trim() : ''
		if (trimmedQuery.length < this.minQueryLength) {
			return
		}

		this.debounceInput()
	}

	async sendQuery() {
		if (this.isLoading || !this.query) {
			return
		}
		this.isLoading = true

		const queries: Array<Promise<any>> = []
		if (this.inputType === 'address') {
			queries.push(this.searchAddresses())
		} else {
			if (this.includeAddresses) {
				queries.push(this.searchAddresses())
			}
			queries.push(this.searchFacilities())
		}
		const results = await Promise.all(queries)
		this.items = results.flat()

		this.isLoading = false
	}

	async searchFacilities(): Promise<Array<FacilityDirection>> {
		let builder = Facility.where('search', this.query)

		if (this.facilityType) {
			builder = builder.option('facilityType', this.facilityType)
		}
		if (this.canTransferLiquidBulk) {
			builder = builder.option('filter[canTransferLiquidBulk]', '1')
		}

		let facilities: Array<Facility> = []
		if (this.query.trim().length >= 3) {
			facilities = ((await builder.get()) as PluralResponse).getData() as Array<Facility>
		}

		return this.mapFacilitiesToDirectionLocations(facilities)
	}

	async searchAddresses(): Promise<Array<any>> {
		const response = (await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${this.query}.json`, {
			params: {
				...MAPBOX_PARAMS,
				limit: this.inputType === 'address' ? 8 : 3
			}
		})) as AxiosResponse<any>

		if (!response.data.features) {
			return []
		}

		return response.data.features.map((item) => {
			return this.createItemFromMapboxFeature(item)
		})
	}

	async setQuery(value) {
		if (typeof value === 'object') {
			this.$emit('input', value)
			return
		}

		if (value === undefined || value === '') {
			this.$emit('input', null)
			return
		}

		if (this.allowFreeText) {
			this.$emit('input', FacilityDirection.fromText(value.trim()))
			return
		}

		if (this.includeAddresses || this.inputType === 'address') {
			const response = (await axios.get(`https://api.mapbox.com/geocoding/v5/mapbox.places/${value}.json`, {
				params: { ...MAPBOX_PARAMS, autocomplete: false }
			})) as AxiosResponse<any>

			if (!response.data.features || response.data.features.length === 0) {
				this.$emit('input', null)
				return
			}

			this.$emit('input', this.createItemFromMapboxFeature(response.data.features[0]))
		}
	}

	get computedLoading() {
		return this.isLoading || this.loading
	}

	onBlur() {
		if (!this.allowFreeText) {
			return
		}

		const trimmedQuery = (this.query || '').trim()
		if (!trimmedQuery || this.value?.text === trimmedQuery) {
			return
		}

		this.$emit('input', FacilityDirection.fromText(trimmedQuery))
	}

	mapFacilitiesToDirectionLocations(facilities) {
		return facilities.map((facility) => {
			const facilityDirectionLocation = new FacilityDirection()
			facilityDirectionLocation.facilityId = facility.getApiId()!
			facilityDirectionLocation.facilityType = facility.facilityType
			facilityDirectionLocation.text = facility.fullName
			facilityDirectionLocation.icon = facility.icon
			facilityDirectionLocation.color = facility.color
			facilityDirectionLocation.coordinates = facility.getLocation()
			facilityDirectionLocation.phoneNumber = facility.phoneNumber
			facilityDirectionLocation.email = facility.email

			return facilityDirectionLocation
		})
	}
}
